// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Portfolio morning briefing (AL-94). Sibling of BriefingCard, not a mode of it:
// the two answer different questions and share almost no chrome.
//
// A hotel briefing states a POSITION ("79.9% tonight, 41 to sell"). A portfolio
// briefing states a COUNT OF PROBLEMS ("2 behind pace, 1 wash risk") plus the one
// cross-hotel number that is honest to aggregate — weighted occupancy. Money is
// deliberately absent: currencies don't add, and a facts strip has no room for a
// per-currency block.
//
// Every finding names a hotel the user must then navigate to, so the deep-link
// chips are the primary affordance here rather than a footnote.

import React from "react";

const nice = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
};

export default function PortfolioBriefingCard({
  briefing,           // ready briefing JSON (or null while streaming)
  streamText,         // accumulating narrative while generating
  streaming,          // bool
  portfolioName,
  hotelCount,
  onOpenHotel,        // (link) => void   link = {property_id, label, question}
  onAskFollowUp,      // () => void
  onDismiss,          // () => void
  onFeedback,         // (fb) => void
}) {
  const facts = (briefing && briefing.facts) || {};
  const pace = facts.pace || {};
  const laggards = pace.laggards || [];
  const wash = facts.wash_risk || [];
  const misses = facts.budget_misses || [];
  const excluded = facts.excluded || [];
  const noBudget = facts.no_budget || [];
  const noComparison = pace.no_comparison || [];
  const links = facts.links || [];
  const allQuiet = !!facts.all_quiet;
  const date = (briefing && briefing.date) || (facts.window || {}).from;
  const text = briefing ? briefing.narrative : streamText;
  const hotels = hotelCount || facts.hotels;

  return (
    <div className="wiz-brief wiz-brief--portfolio">
      <div className="wiz-brief__head">
        <span className="wiz-brief__sun wiz-brief__sun--pf" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18M5 21V8l5-3v16M14 21V11l5-2.5V21" />
            <path d="M8 11h.01M8 14h.01M8 17h.01M17 14h.01M17 17h.01" />
          </svg>
        </span>
        <div className="wiz-brief__titles">
          <span className="wiz-brief__title">Portfolio briefing</span>
          <span className="wiz-brief__sub">
            {portfolioName ? `${portfolioName} · ` : ""}
            {hotels ? `${hotels} hotels · ` : ""}
            {date ? nice(date) : ""}
          </span>
        </div>
        {onDismiss && (
          <button className="wiz-brief__close" onClick={onDismiss} aria-label="Dismiss briefing" title="Dismiss for today">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        )}
      </div>

      <div className="wiz-brief__body">
        {text ? text.split(/\n+/).map((p, i) => <p key={i}>{p}</p>) : null}
        {streaming && (
          <span className="wiz-typing wiz-brief__typing"><span /><span /><span /></span>
        )}
      </div>

      {briefing && (
        <>
          {/* Counts, not positions. "0 behind pace" is a finding worth stating;
              a blank strip would read as missing data. */}
          <div className="wiz-brief__facts">
            {allQuiet ? (
              <span className="wiz-brief__fact wiz-brief__fact--ok">
                Nothing needs a decision across <b>{hotels}</b> hotels
              </span>
            ) : (
              <>
                {laggards.length > 0 && (
                  <span className="wiz-brief__fact wiz-brief__fact--warn">
                    <b>{laggards.length}</b> behind pace
                  </span>
                )}
                {wash.length > 0 && (
                  <span className="wiz-brief__fact wiz-brief__fact--bad">
                    <b>{wash.length}</b> wash risk
                  </span>
                )}
                {misses.length > 0 && (
                  <span className="wiz-brief__fact">
                    <b>{misses.length}</b> missing budget
                  </span>
                )}
              </>
            )}
            {facts.occ_pct != null && (
              <span className="wiz-brief__fact" title={facts.occ_basis || ""}>
                <b>{Math.round(facts.occ_pct)}%</b> portfolio occ
              </span>
            )}
          </div>

          {/* Hotels the numbers above do NOT describe. Naming them is the whole
              point — a silently dropped hotel is how a portfolio total lies. */}
          {(excluded.length > 0 || noComparison.length > 0 || noBudget.length > 0) && (
            <div className="wiz-brief__caveats">
              {excluded.length > 0 && (
                <span>Outside these figures: {excluded.join(", ")}.</span>
              )}
              {noComparison.length > 0 && (
                <span>No last-year data (neither ahead nor behind): {noComparison.join(", ")}.</span>
              )}
              {noBudget.length > 0 && (
                <span>No budget uploaded (neither on nor off target): {noBudget.join(", ")}.</span>
              )}
            </div>
          )}

          {links.length > 0 && onOpenHotel && (
            <div className="wiz-brief__links">
              <div className="wiz-brief__links-lead">Open the hotel that needs you</div>
              <div className="wiz-brief__sources">
                {links.map((l, i) => (
                  <button
                    key={i}
                    className="wiz-source-chip wiz-source-chip--pf"
                    onClick={() => onOpenHotel(l)}
                    title={l.question || ""}
                  >
                    {l.label} ↗
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="wiz-brief__actions">
            {onAskFollowUp && (
              <button className="wiz-brief__follow" onClick={onAskFollowUp}>
                Rank all {hotels} hotels
              </button>
            )}
            {onFeedback && (
              <span className="wiz-fb">
                <button
                  className={`wiz-fb__btn ${briefing.feedback === "up" ? "is-on is-up" : ""}`}
                  onClick={() => onFeedback(briefing.feedback === "up" ? "" : "up")}
                  aria-label="Useful briefing"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zM7 10l4.2-7a2.3 2.3 0 0 1 2.3 2.3V9h5.1a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7" />
                  </svg>
                </button>
                <button
                  className={`wiz-fb__btn ${briefing.feedback === "down" ? "is-on is-down" : ""}`}
                  onClick={() => onFeedback(briefing.feedback === "down" ? "" : "down")}
                  aria-label="Not useful"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3zM17 14l-4.2 7a2.3 2.3 0 0 1-2.3-2.3V15H5.4a2 2 0 0 1-2-2.4l1.4-7a2 2 0 0 1 2-1.6H17" />
                  </svg>
                </button>
              </span>
            )}
            {/* "oldest": four hotels means four feeds. Showing the newest would
                claim a freshness the slowest hotel does not have. */}
            {briefing.context_as_of && (
              <span className="wiz-brief__asof" title="The oldest feed in this portfolio — some hotels may be fresher">
                oldest data as of {String(briefing.context_as_of).slice(0, 10)}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
